import { useQueries } from "@tanstack/react-query";
import type { ModelName, StandardQueryMethodForModel, ReturnForMethod } from "../types";
import type { UseTellescopeQueriesOptions, UseTellescopeQueriesResult, UseTellescopeQueryResult } from "./types";
import { buildTellescopeQueryKey } from "../keys";
import { useTellescopeSession } from "../context";
import { executeTellescopeApiMethod } from "../utils/execute-tellescope-api-method";
import { useMemo } from "react";

/**
 * React hook for executing multiple queries with the same model and method but different request parameters.
 * Only supports standard (non-paginated) query methods. Use `useTellescopeInfiniteQuery` for paginated queries.
 *
 * This hook wraps TanStack Query's `useQueries` hook with Tellescope-specific functionality,
 * providing type-safe access to multiple API calls with the same model/method combination but different parameters.
 * Automatic query key generation and caching are handled for each request.
 *
 * @template Model - The Tellescope model name (e.g., 'users', 'appointments')
 * @template Method - The standard (non-paginated) query method for the specified model (e.g., 'getOne', 'display_names')
 * @template TError - The error type (defaults to Error)
 * @template TData - The returned data type (defaults to the API method's return type)
 *
 * @param options - Queries configuration options
 * @param options.model - The name of the Tellescope model to query
 * @param options.method - The standard query method to call on the model (excludes paginated methods like 'getSome')
 * @param options.requests - Array of request parameters, one for each query to execute
 * @param options.session - Optional Tellescope session to use for all queries instead of the context session
 * @param options.combine - Optional function to transform/combine the results array
 * @param options...tanstackOptions - All TanStack Query options are supported except `queryKey` and `queryFn` (which are automatically generated)
 * @returns Array of TanStack Query result objects (or combined result if combine function is provided), each with data, loading state, error state, refetch utilities, and computed query key
 *
 * @example
 * ```tsx
 * // Fetch multiple users by ID
 * const userResults = useTellescopeQueries({
 *   model: 'users',
 *   method: 'getOne',
 *   requests: [
 *     { id: 'user1' },
 *     { id: 'user2' },
 *     { id: 'user3' }
 *   ]
 * });
 *
 * // Access individual results with full type safety
 * if (userResults[0].isSuccess) {
 *   console.log(userResults[0].data); // Typed as User
 * }
 *
 * // Check loading state across all queries
 * const isAnyLoading = userResults.some(r => r.isLoading);
 *
 * // With TanStack Query options (applied to all queries)
 * const results = useTellescopeQueries({
 *   model: 'users',
 *   method: 'getOne',
 *   requests: [{ id: '1' }, { id: '2' }],
 *   staleTime: 5000,
 *   enabled: isReady
 * });
 *
 * // With combine function to transform results
 * const combinedData = useTellescopeQueries({
 *   model: 'users',
 *   method: 'getOne',
 *   requests: [{ id: '1' }, { id: '2' }, { id: '3' }],
 *   combine: (results) => ({
 *     users: results.map(r => r.data).filter(Boolean),
 *     isLoading: results.some(r => r.isLoading),
 *     hasError: results.some(r => r.isError),
 *   })
 * });
 *
 * // For different models/methods, make separate calls
 * const userResults = useTellescopeQueries({
 *   model: 'users',
 *   method: 'getOne',
 *   requests: [{ id: '1' }, { id: '2' }]
 * });
 *
 * const appointmentResults = useTellescopeQueries({
 *   model: 'appointments',
 *   method: 'getSome',
 *   requests: [
 *     { filter: { userId: '1' } },
 *     { filter: { userId: '2' } }
 *   ]
 * });
 *
 * // For paginated queries, use useTellescopeInfiniteQuery instead:
 * // const { data, fetchNextPage } = useTellescopeInfiniteQuery({
 * //   model: 'users',
 * //   method: 'getSome',
 * //   request: { filter: {} }
 * // });
 * ```
 */
export const useTellescopeQueries = <
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
  TCombinedResult = Array<UseTellescopeQueryResult<Model, Method, TError, TData>>,
>({
  model,
  method,
  requests,
  session: providedSession,
  combine,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: UseTellescopeQueriesOptions<Model, Method, TError, TData, TCombinedResult>): UseTellescopeQueriesResult<
  Model,
  Method,
  TError,
  TData,
  TCombinedResult
> => {
  const session = useTellescopeSession(providedSession);

  // Memoize query keys for all requests
  const queryKeys = useMemo(
    () => requests.map((request) => buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey })),
    [requests, model, method, requestKeysToOmitFromQueryKey]
  );

  // Build the queries array for TanStack Query's useQueries
  const queriesWithFunctions = useMemo(
    () =>
      requests.flatMap((request, index) => {
        const queryKey = queryKeys[index];

        if (!queryKey) return [];

        return [
          {
            queryKey,
            queryFn: () => {
              if (session === null) {
                throw new Error(
                  "No Tellescope session provided for query. Queries should be disabled if session is `null`"
                );
              }

              return executeTellescopeApiMethod({
                session,
                model,
                method,
                request,
                parseISOStrings: true, // TODO: improve this by making it an option locally and globally
              });
            },
            ...tanstackOptions,
            // Disable all queries if session is null, but respect individual enabled flags
            enabled: !!session && (tanstackOptions?.enabled ?? true),
          },
        ];
      }),
    [model, method, requests, queryKeys, session, tanstackOptions]
  );

  // Execute queries (without combine - we'll handle it after augmentation)
  const results = useQueries({
    queries: queriesWithFunctions,
  });

  // TODO: fix referential instability
  // Augment results with query keys and optionally apply combine function
  // @ts-expect-error TypeScript can't narrow TCombinedResult based on the combine conditional.
  // When combine is undefined, TCombinedResult defaults to Array<UseTellescopeQueryResult>, which matches augmentedResults.
  // When combine is provided, it returns TCombinedResult explicitly. Both branches are type-safe at runtime.
  return useMemo<TCombinedResult>(() => {
    // First, augment all results with their query keys
    // The spread operation correctly adds queryKey to each result, maintaining all properties from UseQueryResult.
    // This is safe because we're only adding the queryKey field, not removing or modifying existing fields.
    const augmentedResults = results.flatMap<UseTellescopeQueryResult<Model, Method, TError, TData>>(
      // @ts-expect-error TanStack Query's useQueries returns complex union types that don't perfectly align with our augmented result type.
      (result, index) => {
        const queryKey = queryKeys[index];
        if (!queryKey) return [];

        return [
          {
            ...result,
            // queryKeys is built from requests with the same indices; access is always in bounds
            queryKey,
          },
        ];
      }
    );

    // If combine function was provided, apply it to the augmented results
    if (combine) {
      return combine(augmentedResults);
    }

    // Otherwise, return the augmented results array
    return augmentedResults;
  }, [results, queryKeys, combine]);
};
